import {Injectable} from "@angular/core";
import {Settings} from "~/app/core/model/settings";
import {StorageService} from "~/app/core/storage.service";
import * as Log from "~/app/core/log";
import * as _ from "lodash";
import {AngularFireRemoteConfig} from "@angular/fire/compat/remote-config";
import {take} from "rxjs/operators";
import {BehaviorSubject, Observable} from "rxjs";

@Injectable()
export class SettingsService {
    private settingsObservable = new BehaviorSubject<Settings>(null);
    constructor(
        private readonly _storageService: StorageService,
        private readonly remoteConfig: AngularFireRemoteConfig,

    ) {
        // nothing
        this.updateLogSettings(this.loadSettings());
    }

    observeSettings(): Observable<Settings> {
        if (this.settingsObservable.value === null) this.settingsObservable.next(this.loadSettings());
        return this.settingsObservable.asObservable();
    }

    loadSettings(): Settings {
        const settingVals = this._storageService.loadSettings();
        const settings = new Settings();
        if (!settingVals) {
            this._storageService.storeSettings(settings);
        } else {
            settings.deserialize(settingVals);
        }
        if (!settings.gamificationEnabled) {
            settings.gamificationMysteryMode = false;
            settings.gamificationDailyProgressEnabled = false;
            settings.gamificationFavoriteMode = false;
        }
        settings.env = "prod";
        // settings.env = "local";  // Important! Comment out for prod deploy!
        // settings.localUrl = "http://127.0.0.1:8080/test";
        return settings;
    }

    saveSettings(settings: Settings, lastSettings?: Settings): void {
        if (!lastSettings) lastSettings = this.loadSettings();
        Log.setDefaultLevel(settings.logLevel);
        this.updateLogSettings(settings, lastSettings);
        this.updateEnvSettings(settings, lastSettings);
        this._storageService.storeSettings(settings);
        this.settingsObservable.next(settings);
        this.refreshRemoteSettings();
    }

    preconfigureSettings(mode: "production" | "beta" | "testmode"): Settings {
        const settings = this.loadSettings();
        settings.preconfigureSettings(mode);
        this.saveSettings(settings);
        return settings;
    }

    refreshRemoteSettings(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            const settings = this.loadSettings();
            if (settings.loadRemoteSettings) {
                Log.debug(Log.Tag.SettingsService, "Load and apply remote settings");

                const properties: any = {};

                [
                    {key: "search_alert_delay_seconds", default: 30},
                    {key: "sponsor_video_next_seconds", default: 60 * 5},
                    {key: "sponsor_video_skip_delay_seconds", default: 10},
                    {key: "enforce_redeem_location", default: true},
                    {key: "enforce_redeem_net", default: false},
                    {key: "redeem_allow_miles", default: 0.5},
                    {key: "redeem_now_miles", default: 0.1},
                    {key: "sign_up_before_no_offers", default: true},
                    {key: "sign_up_before_any_offers", default: true},
                    {key: "obscure_unauthenticated_offers", default: true},
                    {key: "reminder_notification_enabled", default: false},
                    {key: "quick_share", default: true},
                    {key: "quick_scroll", default: false},
                    {key: "allow_reshow_cashier", default: true},
                    {key: "show_share_coupon", default: true},
                    {key: "show_business_coupon", default: true},
                    {key: "show_miles_away_on_coupon", default: true},
                    {key: "poll_thumbs_up_before_bounceback", default: false},
                    {key: "always_show_bouncebacks", default: false},
                    {key: "allow_filtering", default: false},
                    {key: "filtering_min_offers", default: 0},
                    {key: "combined_offers_tab", default: false},
                    {key: "interleaved_coupons", default: true},
                    {key: "gamification_enabled", default: false},
                    {key: "gamification_daily_progress_enabled", default: false},
                    {key: "gamification_mystery_mode", default: false},
                    {key: "gamification_favorite_mode", default: false},
                    {key: "favorite_cost", default: 1},
                    {key: "sort_offers_local", default: true},
                    {key: "sort_online_priority_shift", default: 100},
                    {key: "swipe_sensitivity", default: 3},

                    {key: "earn_tickets_sign_up_friend", default: 10},
                    {key: "earn_tickets_sign_up_from_referral", default: 10},
                    {key: "earn_tickets_redeem_local", default: 10},
                    {key: "earn_tickets_redeem_online", default: 3},
                    {key: "earn_tickets_open_app", default: 2},
                    {key: "earn_tickets_share", default: 2},
                    {key: "earn_tickets_open_offer", default: 1},
                    {key: "earn_tickets_filter_offers", default: 1},
                    {key: "earn_tickets_visit_website", default: 1},
                    {key: "earn_tickets_favorite_offer", default: 1}
                ].forEach(v => {
                    properties[v.key] = v.default;
                });

                this.remoteConfig.strings.pipe(take(1)).subscribe(value => {
                    for (let valueKey in value) {
                        properties[valueKey] = value[valueKey];
                    }
                });

                const originalSettings = this.loadSettings();
                const newSettings = this.loadSettings();
                newSettings.searchAlertDelaySeconds = Number(properties.search_alert_delay_seconds);
                newSettings.sponsorVideoNextSeconds = Number(properties.sponsor_video_next_seconds);
                newSettings.sponsorVideoSkipDelaySeconds = Number(properties.sponsor_video_skip_delay_seconds);
                newSettings.enforceRedeemLocation = Boolean(properties.enforce_redeem_location);
                newSettings.enforceRedeemNet = Boolean(properties.enforce_redeem_net);
                newSettings.redeemAllowedMiles = Number(properties.redeem_allow_miles);
                newSettings.redeemNowMiles = Number(properties.redeem_now_miles);
                newSettings.signupBeforeNoOffers = Boolean(properties.sign_up_before_no_offers);
                newSettings.signupBeforeAnyOffers = Boolean(properties.sign_up_before_any_offers);
                newSettings.obscureUnauthenticatedOffer = Boolean(properties.obscure_unauthenticated_offers);
                newSettings.reminderNotificationEnabled = Boolean(properties.reminder_notification_enabled);
                newSettings.quickShare = Boolean(properties.quick_share);
                newSettings.quickScroll = Boolean(properties.quick_scroll);
                newSettings.allowReshowCashier = Boolean(properties.allow_reshow_cashier);
                newSettings.showShareCoupon = Boolean(properties.show_share_coupon);
                newSettings.showBusinessCoupon = Boolean(properties.show_business_coupon);
                newSettings.showMilesAwayOnCoupon = Boolean(properties.show_miles_away_on_coupon);
                newSettings.pollThumbsUpBeforeBounceback = Boolean(properties.poll_thumbs_up_before_bounceback);
                newSettings.alwaysShowBouncebacks = Boolean(properties.always_show_bouncebacks);
                newSettings.allowFiltering = Boolean(properties.allow_filtering);
                newSettings.filteringMinOffers = Number(properties.filtering_min_offers);
                newSettings.combinedOffersTab = Boolean(properties.combined_offers_tab);
                newSettings.interleavedCoupons = Boolean(properties.interleaved_coupons);
                newSettings.gamificationEnabled = Boolean(properties.gamification_enabled);
                newSettings.gamificationDailyProgressEnabled = Boolean(properties.gamification_daily_progress_enabled);
                newSettings.gamificationMysteryMode = Boolean(properties.gamification_mystery_mode);
                newSettings.gamificationFavoriteMode = Boolean(properties.gamification_favorite_mode);
                newSettings.favoriteCost = Number(properties.favorite_cost);
                newSettings.swipeSensitivity = Number(properties.swipe_sensitivity);
                newSettings.sortOffersLocal = Boolean(properties.sort_offers_local);
                newSettings.sortOnlinePriorityShift = Number(properties.sort_online_priority_shift);

                newSettings.earnTicketsSignUpFriend = Number(properties.earn_tickets_sign_up_friend);
                newSettings.earnTicketsSignUpFromReferral = Number(properties.earn_tickets_sign_up_from_referral);
                newSettings.earnTicketsRedeemLocal = Number(properties.earn_tickets_redeem_local);
                newSettings.earnTicketsRedeemOnline = Number(properties.earn_tickets_redeem_online);
                newSettings.earnTicketsOpenApp = Number(properties.earn_tickets_open_app);
                newSettings.earnTicketsShare = Number(properties.earn_tickets_share);
                newSettings.earnTicketsOpenOffer = Number(properties.earn_tickets_open_offer);
                newSettings.earnTicketsFilterOffers = Number(properties.earn_tickets_filter_offers);
                newSettings.earnTicketsVisitWebsite = Number(properties.earn_tickets_visit_website);
                newSettings.earnTicketsFavoriteOffer = Number(properties.earn_tickets_favorite_offer);

                const diff = this.difference(originalSettings, newSettings);
                if (Object.keys(diff).length > 0) {
                    Log.debug(Log.Tag.SettingsService, "Settings changed");
                    Log.debug(Log.Tag.SettingsService, diff);
                    this._storageService.storeSettings(newSettings);
                    this.settingsObservable.next(settings);
                    resolve(true);
                } else {
                    resolve(false);
                }
            }
        });
    }

    private updateLogSettings(settings: Settings, lastSettings?: Settings) {
        if (!lastSettings || settings.enableLogging !== lastSettings.enableLogging) {
            if (settings.enableLogging) {
                Log.setLogBufferSize(settings.logBufferSize);
            } else {
                Log.setLogBufferSize(0);
            }
        } else if (settings.enableLogging) {
            if (settings.logBufferSize !== lastSettings.logBufferSize) {
                Log.setLogBufferSize(settings.logBufferSize);
            }
        }
        Log.setDefaultLevel(settings.logLevel);
    }

    private updateEnvSettings(settings: Settings, lastSettings?: Settings) {
        if (lastSettings && settings.env !== lastSettings.env) {
            // changing environment, so need to clear data
            this._storageService.resetData();
            this.settingsObservable.next(this.loadSettings());
        }
    }

    /**
     * Deep diff between two object, using lodash
     * @param  {Object} object Object compared
     * @param  {Object} base   Object to compare with
     * @return {Object}        Return a new object who represent the diff
     */
    private difference(src, cmp) {
        const changes = (object, base) => {
            return _.transform(object, (result, value, key) => {
                if (!_.isEqual(String(value), String(base[key]))) {
                    Log.trace(Log.Tag.SettingsService, "DIFFERENT " + String(key) + ": " + value + " != " + base[key]);
                    result[key] = (_.isObject(value) && _.isObject(base[key])) ? changes(value, base[key]) : value;
                }
            });
        }
        return changes(cmp, src);
    }
}
